import { useRef, useEffect, MutableRefObject } from 'react';
import { InterpreterFrom } from 'xstate';
import { useActor } from '@xstate/react';
import { ArrowDropDown, SmartButton, Title } from '@styled-icons/material';
import { ArrowRight, Trash, Plus, Link as LinkIcon, MousePointer } from '@styled-icons/feather';
import classNames from 'classnames';
import {
  backgroundColors,
  colorsGradient,
  computeColor,
  DEFAULT_ACCENT_NAME,
  NavbarItem,
  textColors,
  textColorsBasic,
} from '@typedream/core';
import { menuOptionsMachine } from '../../../nodes/navbar/fsm/MenuOptions.fsm';
import Menu from '../Menu';
import styles from './NavbarSettingToolbar.module.css';
import { LeftMenuItem } from '../LeftMenu';
import { useOutsideEventWithCanvas } from '../../../utils/eventAlerter';
import SelectColorNavbar from '../SelectColor';
import { LinkSuggestion } from '../../SideToolbar/items/Link';
import { getTPlugin, useTPlateEditorRef } from '../../../plate';
import { InternalLinkOptions } from '../../../plugins/internalLink/types';
import { KEY_INTERNAL_LINK } from '../../../pluginKeys';
import sidebarStyles from '../../SideToolbar/SideToolbar.module.css';
import sharedStyles from '../Shared.module.css';
import { MenuSectionVertical } from '../Menu/MenuSectionVertical';
import { getSelectedNavbarItem } from '../../OverlayToolbar/items/Navbar/utils';

// TODO(@eibedwi): move to constant
export const DEFAULT_SIDEBAR_WIDTH = 250;

// TODO(@ibedwi): move to assets
const fonts = {
  inter: 'Inter',
  libre_caslon_text: 'Libre Caslon Text',
  space_mono: 'Space Mono',
  quicksand: 'Quicksand',
};
export const DEFAULT_FONT_FAMILY_NAME = 'inter';
export const FONT_FAMILIES_DEFAULT = {
  [DEFAULT_FONT_FAMILY_NAME]: fonts.inter,
};

export function FontIcon() {
  return (
    <div
      style={{
        textAlign: 'center',
        verticalAlign: 'center',
        lineHeight: '20px',
        height: '20px',
        width: '20px',
        cursor: 'pointer',
      }}
    >
      Tt
    </div>
  );
}

export function NavbarSettingToolbar(props: {
  width: number;
  machine: InterpreterFrom<typeof menuOptionsMachine>;
  // eslint-disable-next-line react/require-default-props
  navbarItemRef?: MutableRefObject<HTMLDivElement>;
}) {
  const { width, machine, navbarItemRef } = props;
  const editor = useTPlateEditorRef();
  const { options } = getTPlugin<InternalLinkOptions>(editor, KEY_INTERNAL_LINK);

  const FONT_FAMILIES = { ...FONT_FAMILIES_DEFAULT, ...fonts };

  const ref = useRef<HTMLDivElement | null>(null);
  const [machineState, send] = useActor(machine);
  useEffect(() => {
    const el = ref.current;
    if (machineState.hasTag('ui:show_setting_menu') && el !== null) {
      const parent = el.parentElement;
      const rect = parent!.getBoundingClientRect();
      const navbarItemEl = navbarItemRef?.current;
      const navbarItemElRect = navbarItemEl?.getBoundingClientRect();

      const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
      const maxLeft = !machineState.hasTag('ui:show_sidebar') ? 0 : DEFAULT_SIDEBAR_WIDTH;
      // const maxRight = vw - SIDETOOLBAR_WIDTH;
      const maxRight = vw;
      const paddingLeft = 0;
      const paddingRight = 20;

      const elementWidth = width;

      const leftRaw = 0;
      const rightRaw = elementWidth;
      const leftStop = maxLeft + paddingLeft - rect.left - window.pageXOffset;
      const rightStop = maxRight - paddingRight - rect.left - window.pageXOffset;

      const left =
        leftRaw < leftStop
          ? Math.max(leftRaw, leftStop)
          : Math.min(rightRaw - elementWidth, rightStop - elementWidth);

      el.style.opacity = '1';
      el.style.top = '0px';
      if (rect.right > maxRight) {
        el.style.right = `${elementWidth - (navbarItemElRect?.width ?? 0)}px`;
        el.style.left = 'auto';
        el.style.width = '100%';
      } else {
        el.style.left = `${left}px`;
      }
    } else if (ref.current && el !== null) {
      el.removeAttribute('style');
    }
  }, [machineState, ref.current]);

  const outsideDetectRef = useOutsideEventWithCanvas<HTMLDivElement>('click', () => {
    send('ON_SETTING_MENU_BLUR');
  });

  const getCurrentLinkPageName = ({
    pageId,
    isInternalLink,
  }: {
    pageId?: string;
    isInternalLink: boolean;
  }) => {
    if (isInternalLink && pageId) {
      return options?.pagesPaths?.[pageId]?.name ?? '';
    }
    return undefined;
  };

  const getTitle = () => {
    if (machineState.context.menuType === 'brand') {
      return machineState.context.dataNavbar?.logoTitle;
    }

    const navbarItem = getSelectedNavbarItem(machineState.context);
    if (navbarItem) {
      return navbarItem.title;
    }

    return undefined;
  };

  const getUrl = () => {
    if (machineState.context.menuType === 'brand') {
      return machineState.context.dataNavbar?.logoURL;
    }

    const navbarItem = getSelectedNavbarItem(machineState.context);
    if (navbarItem) {
      return navbarItem.url;
    }

    return undefined;
  };

  const getInternalLink = () => {
    if (machineState.context.menuType === 'brand') {
      return machineState.context.dataNavbar?.logoURLInternalLink;
    }

    const navbarItem = getSelectedNavbarItem(machineState.context);
    if (navbarItem) {
      return navbarItem.internalLink;
    }

    return undefined;
  };

  const getUrlPageId = () => {
    if (machineState.context.menuType === 'brand') {
      return machineState.context.dataNavbar?.logoURLPageID;
    }

    const navbarItem = getSelectedNavbarItem(machineState.context);
    if (navbarItem) {
      return navbarItem.pageId;
    }

    return undefined;
  };

  const getIsInternalLink = () => {
    if (machineState.context.menuType === 'brand') {
      return machineState.context.dataNavbar?.logoURLInternalLink;
    }

    const navbarItem = getSelectedNavbarItem(machineState.context);
    if (navbarItem) {
      return navbarItem.internalLink;
    }

    return undefined;
  };

  const getUrlOpenInNewTab = () => {
    if (machineState.context.menuType === 'brand') {
      return machineState.context.dataNavbar?.logoOpenInNewTab;
    }

    const navbarItem = getSelectedNavbarItem(machineState.context);
    if (navbarItem) {
      return navbarItem.openInNewTab;
    }

    return undefined;
  };

  const getComputedTextColor = () => {
    if (machineState.context.menuType === 'brand') {
      return computeColor(machineState.context.dataNavbar?.logoColor, {
        ...textColorsBasic,
        ...colorsGradient,
      });
    }

    const navbarItem = getSelectedNavbarItem(machineState.context);
    if (navbarItem) {
      return computeColor(navbarItem.color, { ...textColorsBasic, ...colorsGradient });
    }

    return undefined;
  };

  const { menuType, dataNavbar, menuIndex } = machineState.context;
  const isGradientEnabled =
    menuType === 'brand' ||
    (menuType &&
      dataNavbar &&
      menuIndex !== undefined &&
      dataNavbar[menuType][menuIndex].type !== 'button');
  const navbarItem = getSelectedNavbarItem(machineState.context) as NavbarItem;

  return (
    <div className={styles.navbarMenuOptionsContainer} ref={outsideDetectRef}>
      <Menu className="NavbarMenu_Menu" style={{ width }}>
        <div className={styles.navbarMenuOptions} data-testid="NavbarMenuOptions">
          <div className={classNames(sharedStyles.optionTitle, styles.title)}>Settings</div>
          <LeftMenuItem
            icon={<Title size={15} />}
            title={
              <input
                className={classNames(styles.input, styles.menuInput)}
                type="text"
                onFocus={() => send('ON_SELECT_TITLE')}
                value={getTitle() ?? '-'}
                placeholder="Logo name"
                onChange={(e) => {
                  send({ type: 'ON_CHANGE_TITLE', newTitle: e.target.value });
                }}
                onBlur={() => send('ON_BLUR_TITLE')}
                data-testid="NavbarMenuOptions_ChangeTitle"
              />
            }
          />
          <LeftMenuItem
            icon={<LinkIcon size={15} />}
            title={
              <>
                <input
                  className={classNames(styles.input, styles.menuInput)}
                  type="text"
                  onClick={() => send('ON_SELECT_LINK_MENU')}
                  onFocus={() => send('ON_SELECT_LINK_MENU')}
                  value={getUrl()}
                  placeholder="URL"
                  onChange={(e) => {
                    send({ type: 'ON_CHANGE_LINK', newLink: { url: e.target.value } });
                  }}
                  data-testid="NavbarMenuOptions_ChangeUrl"
                />
                {getUrl() && (
                  <div className="with-lighter-text-color is-size-7 ml-2">
                    {getInternalLink()
                      ? `Page: ${getCurrentLinkPageName({
                          pageId: getUrlPageId(),
                          isInternalLink: getIsInternalLink() ?? false,
                        })}`
                      : 'External Link'}
                  </div>
                )}
              </>
            }
            onMenuClose={() => {
              send({ type: 'ON_BLUR_LINK_MENU' });
            }}
            menuOpen={machineState.hasTag('ui:show_link_menu') && getUrl() !== ''}
          >
            <div className={styles.linkSuggestionContainer}>
              <LinkSuggestion
                onSave={(url, pageID, pagePath) => {
                  send({ type: 'ON_CHANGE_LINK', newLink: { url, pageID, pagePath } });
                }}
                inputURL={getUrl() ?? ''}
                internalPages={options?.pagesPaths}
              />
            </div>
          </LeftMenuItem>
          {
            // Only show the open in new tab toggle if there's
            // URL
            getUrl() && (
              <div
                className="pr-3 pb-0 is-flex is-align-items-center is-justify-content-space-between"
                style={{ paddingLeft: '2.5em' }}
              >
                <div className={classNames(sidebarStyles.flex1)}>
                  <div className="with-lighter-text-color is-size-7">Open in new tab</div>
                </div>

                <div className={sidebarStyles.switchContainer}>
                  <input
                    id="toolbar-link-input"
                    type="checkbox"
                    name="publish"
                    className="switch is-rounded is-small is-primary"
                    checked={getUrlOpenInNewTab()}
                    onClick={() => {}}
                    onChange={(e) => {
                      send({
                        type: 'ON_CHANGE_LINK_OPEN_NEW_TAB',
                        newLinkOpenInNewTab: e.target.checked,
                      });
                    }}
                  />
                  <label htmlFor="toolbar-link-input" style={{ verticalAlign: 'middle' }}>
                    {/* no text label */}
                  </label>
                </div>
              </div>
            )
          }
          {(machineState.context.menuType === 'menu' ||
            machineState.context.menuType === 'endMenu') &&
            machineState.context.menuIndex !== undefined && (
              <MenuSectionVertical>
                <LeftMenuItem
                  title="Type"
                  icon={<MousePointer size={15} />}
                  command={<ArrowRight size={15} />}
                  onClick={() => send({ type: 'ON_SELECT_TYPE_MENU' })}
                  onMenuClose={() => {
                    send({ type: 'ON_BLUR_TYPE_MENU' });
                  }}
                  menuOpen={machineState.hasTag('ui:show_type_menu')}
                >
                  <LeftMenuItem
                    icon={<LinkIcon size={15} />}
                    title="Link"
                    onClick={() => {
                      send({ type: 'ON_CHANGE_TYPE', newMenuItemType: 'link' });
                    }}
                  />
                  <LeftMenuItem
                    icon={<SmartButton size={15} />}
                    title="Button"
                    onClick={() => {
                      send({ type: 'ON_CHANGE_TYPE', newMenuItemType: 'button' });
                    }}
                  />
                  <LeftMenuItem
                    icon={<ArrowDropDown size={15} />}
                    title="Dropdown"
                    onClick={() => {
                      send({ type: 'ON_CHANGE_TYPE', newMenuItemType: 'dropdown' });
                    }}
                  />
                </LeftMenuItem>
                <LeftMenuItem
                  title="Delete Menu Item"
                  icon={<Trash size={15} />}
                  onClick={() =>
                    send({
                      type: 'ON_DELETE_MENU',
                      menuIndex: machineState.context.menuIndex!,
                      menuType: machineState.context.menuType!,
                    })
                  }
                />
              </MenuSectionVertical>
            )}
          <MenuSectionVertical>
            <LeftMenuItem
              title="Font Family"
              icon={<FontIcon />}
              command={<ArrowRight size={15} />}
              onClick={() => send({ type: 'ON_CLICK_FONT' })}
              onMenuClose={() => {
                send({ type: 'ON_BLUR_FONT_MENU' });
              }}
              menuOpen={machineState.hasTag('ui:show_font_list')}
            >
              {Object.entries(fonts).map(([k, v]) => (
                <LeftMenuItem
                  key={k}
                  title={
                    <span
                      style={{
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore - TODO(@ibedwi): fix type
                        fontFamily: FONT_FAMILIES[k],
                        cursor: 'pointer',
                      }}
                    >
                      {v}
                    </span>
                  }
                  onClick={() => {
                    send({ type: 'ON_CHANGE_FONT', newFont: k });
                  }}
                />
              ))}
            </LeftMenuItem>

            {!isGradientEnabled && (
              <LeftMenuItem
                title="Background Color"
                icon={
                  <div
                    className={sharedStyles.colorIcon}
                    style={{
                      background: computeColor(
                        'background' in navbarItem ? navbarItem.background : 'default',
                        { ...backgroundColors }
                      ),
                    }}
                  />
                }
                command={<ArrowRight size={15} />}
                onClick={() => send({ type: 'ON_CLICK_BACKGROUND_COLOR_MENU' })}
                onMenuClose={() => {
                  send({ type: 'ON_BLUR_BACKGROUND_COLOR_MENU' });
                }}
                menuOpen={machineState.hasTag('ui:show_background_color_list')}
              >
                <SelectColorNavbar
                  title="Background Colors"
                  onSelect={(color) =>
                    send({ type: 'ON_CHANGE_BACKGROUND_COLOR', background: color })
                  }
                  colors={[
                    [DEFAULT_ACCENT_NAME, backgroundColors[DEFAULT_ACCENT_NAME]],
                    Object.entries(textColorsBasic),
                    Object.entries(colorsGradient),
                  ]}
                  currentColor={'background' in navbarItem ? navbarItem.background : 'default'}
                  iconStyle="background"
                />
              </LeftMenuItem>
            )}

            <LeftMenuItem
              title="Text Color"
              icon={
                <div
                  className={sharedStyles.colorIcon}
                  style={{
                    background: getComputedTextColor(),
                  }}
                />
              }
              command={<ArrowRight size={15} />}
              onClick={() => send({ type: 'ON_CLICK_TEXT_COLOR_MENU' })}
              onMenuClose={() => {
                send({ type: 'ON_BLUR_COLOR_MENU' });
              }}
              menuOpen={machineState.hasTag('ui:show_color_list')}
            >
              <SelectColorNavbar
                title="Text Colors"
                onSelect={(color) => send({ type: 'ON_CHANGE_COLOR', newColor: color })}
                colors={[
                  [DEFAULT_ACCENT_NAME, textColors[DEFAULT_ACCENT_NAME]],
                  Object.entries(textColorsBasic),
                  ...(isGradientEnabled ? [Object.entries(colorsGradient)] : []),
                ]}
              />
            </LeftMenuItem>
            {machineState.context.menuType === 'brand' && (
              <LeftMenuItem
                icon={
                  <img
                    src="/logo.svg"
                    alt="logo"
                    width={15}
                    style={{ filter: 'grayscale(100%)' }}
                  />
                }
                title={machineState.context.dataNavbar?.logoType ? 'Remove Logo' : 'Add Logo'}
                command={
                  machineState.context.dataNavbar?.logoType ? (
                    <Trash size={15} />
                  ) : (
                    <Plus size={15} />
                  )
                }
                onClick={() => send('ON_CLICK_LOGO_MENU')}
              />
            )}
          </MenuSectionVertical>
        </div>
      </Menu>
    </div>
  );
}
